import React, { useEffect, useState, useRef } from 'react'
import GridSearchTemplate from '../../../pages/GridSearchTemplate'
import { DatePicker } from '../../shared/DatePicker'
import { useTextResources } from '../../../language'
import { Form } from 'semantic-ui-react'
import HotelsDropDown from '../../shared/HotelsDropDown'
import UsersDropDown from '../../shared/UsersDropDown'
import PaymentTypesDropDown from '../../shared/PaymentTypesDropDown'
import TextInput from '../../shared/TextInput'
import { GetDetailedCashReport } from '../../../api/services/finance'
import moment from 'moment'
import PropTypes from 'prop-types'
import { endpoint } from '../../../api/http'
import { onError } from '../../shared/messages'
import { GetHotels } from '../../../api/services/organization'
import { dateFormat } from '../../shared/constants'
import { createExcelHeader } from '../../GridPanel/utility'

const DetailedCashReport = ({ HotelID }) => {
    const [state, setState] = useState([])
    const [hotels, setHotels] = useState([])

    //Latest chosen dates, used for the Excel file title, saved in ref to avoid unnecessary rerenders
    const dateRangeRef = useRef({})

    useEffect(()=>{
        (async ()=>{
            try {
                const hotels = await GetHotels()
                setHotels(hotels)
            } catch (error) {
                onError(error)
            }
        })()
    }, [HotelID])

    const text = useTextResources()

    const getFormattedPaymentDetailsString = (row) => {

        if (row?.BankID) {
            return `Check Details: ${row.BankID} - ${row.BranchCode} - ${row.AccountNumber} - ${row.CheckNumber} - ${row.CheckDate}`;
        }
        if (row?.CcDigits) {
            return `Credit Card 4 Digits: ${row.CcDigits}`;
        }
        return 'None';
    };

    const searchCompleteHandler = (data, searchStateFromGrid) => {
        dateRangeRef.current.StartDate = searchStateFromGrid.StartDate
        dateRangeRef.current.EndDate = searchStateFromGrid.EndDate

        const filteredData = data.filter(row => {
            const paymentDetailsToFilter = getFormattedPaymentDetailsString(row)?.toLowerCase();
            const paymentDetailsCondition = !searchStateFromGrid.PaymentDetails || paymentDetailsToFilter.includes(searchStateFromGrid.PaymentDetails.toLowerCase());

            // Check each condition individually only if the corresponding search term is provided
            return (!searchStateFromGrid.PaymentDetails || paymentDetailsCondition)
        }).map(row => ({...row, PaymentType: row.PaymentType || text('None') }));

        setState(filteredData);
    }

    const getPaymentDetailsCellValue = ( row ) => {
        return getFormattedPaymentDetailsString(row)
    }

    const folioFormatter = ({ value }) => {
        return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}Finance/Folio/Folio?FolioId=${value}`}>{value}</a>
    }

    const reservationFormatter = ({ value, row }) => {
        if (row) {
            return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}PMS/Reservations/Index/${row.ReservationsID}?hotelid=${HotelID}`}>{value}</a>
        } else {
            return value
        }
    }
    const amountFormatter = ({value})=>{
        return value ? value.toFixed(2) : 0.00
    }

    const dateFormatter = ({ value }) => {
        return value && moment(value).format(dateFormat)
    }

    const getCellValueForExport = (row, name) => {
        switch (name) {
            case "Payment": getFormattedPaymentDetailsString(row)
                break
            case "TransactionDate":
            case "BusinessDate":
                return moment(row[name]).format(dateFormat)
            default:
                return row[name]
        }
    }

    const getExcelHeaderTitle = () => {
        const hotel = hotels.find(x => x.Id === HotelID)
        return `Detailed cash report ${hotel && hotel.Name}, ${moment(dateRangeRef.current.StartDate).format(dateFormat)} - ${moment(dateRangeRef.current.EndDate).format(dateFormat)}`;
    }

    const gridOptions = {
        search: {
            title: "Detailed cash report",
            fields: [
                <Form.Field control={HotelsDropDown} clearable width="4" label={text("Hotel")} key="HotelID" />,
                <DatePicker
                    type="basic"
                    label={text("StartDate")}
                    name="StartDate"
                    key="StartDate"
                />,
                <DatePicker
                    type="basic"
                    label={text("EndDate")}
                    name="EndDate"
                    key="EndDate"
                />,
                <Form.Field control={UsersDropDown} multiple clearable width="5" label={text("Clerks")} key="ClerksIDs" />,
                <Form.Field
                key='FolioId'
                label={text('Folio')}
                control={TextInput}
                placeholder={text('Folio')}
                clearable
                />,
                <Form.Field
                key='ReservationNumber'
                label={text('ReservationNumber')}
                control={TextInput}
                placeholder={text('ReservationNumber')}
                clearable
                />,
                <Form.Field
                key='PaymentDetails'
                label={text('PaymentDetails')}
                control={TextInput}
                placeholder={text('PaymentDetails')}
                clearable
                />,
                <Form.Field
                placeholder={text('All')}
                key='PaymentTypes'
                label={text('PaymentTypes')}
                control={PaymentTypesDropDown}
                multiple
                clearable
                />,
            ],
            initial: {
                "StartDate": moment().toDate(),
                "EndDate": moment().toDate(),
                "HotelID": HotelID
            },
            api: GetDetailedCashReport,
            groupSize: 6
        },
        grid: {
            columns: [
                { name: 'PaymentType', title: 'Payment Type' },
                { name: 'TransactionName', title: 'Transaction' },
                { name: 'FolioID', title: 'Folio', formatter: folioFormatter },
                { name: 'RoomName', title: 'Room' },
                { name: 'InvoiceID', title: 'Invoice' },
                { name: 'ReservationNumber', title: 'Reservation', formatter: reservationFormatter },
                { name: 'GuestFullName', title: 'Folio Owner' },
                { name: 'HotelCurrencyAmount', title: 'Amount' ,formatter: amountFormatter},
                { name: 'ForeignCurrencyAmount', title: 'F.C. Amount' ,formatter: amountFormatter },
                { name: 'ForeignCurrency', title: 'F. Currency' },
                { name: 'TransactionDate', title: 'Transaction Date', formatter: dateFormatter },
                { name: 'BusinessDate', title: 'Business Date' },
                { name: 'ClerkName', title: 'Clerk' },
                { name: 'AgentName', title: 'Agent' },
                { name: 'CompanyName', title: 'Company' },
                { name: 'GuestName', title: 'Guest' },
                { name: 'Payment', title: 'Payment Details', getCellValue: getPaymentDetailsCellValue },
                { name: 'ReceiptID', title: 'Receipt ID' },
                { name: 'Voucher', title: 'Voucher' },
                { name: 'Comments', title: 'Comments' },
            ],
            pdf: true,
            cacheId:'DetailedCash',
            customOptions:{
              group:{
                grouping: [{ columnName: "PaymentType" }, { columnName: "TransactionName" }],
                defaultExpandedGroups: ["Cash", "Check"]
              }
            },
            summary: true,
            export: true,
            totalSummaryItems: [
                { columnName: 'HotelCurrencyAmount', type: 'sum' },
                { columnName: 'ForeignCurrencyAmount', type: 'sum' }
            ],
            groupSummaryItems: [
                { columnName: 'HotelCurrencyAmount', type: 'sum', showInGroupFooter: false, alignByColumn: true },
                { columnName: 'ForeignCurrencyAmount', type: 'sum', showInGroupFooter: false, alignByColumn: true }
            ],
            columnExtensions: [
                { columnName: 'Payment', width: 250 },
            ],
            filtering: true,
            excel: {
                getCellValue: getCellValueForExport,
                grouping: [{ columnName: "PaymentType" }, { columnName: "TransactionName" }],
                totalSummaryItems: [
                    { columnName: 'HotelCurrencyAmount', type: 'sum' },
                    { columnName: 'ForeignCurrencyAmount', type: 'sum' }
                ],
                groupSummaryItems: [
                    { columnName: 'HotelCurrencyAmount', type: 'sum', showInGroupFooter: false, alignByColumn: true },
                    { columnName: 'ForeignCurrencyAmount', type: 'sum', showInGroupFooter: false, alignByColumn: true }
                ],
                customizeHeader: createExcelHeader(getExcelHeaderTitle())
            }
        }
    }
    return (<GridSearchTemplate data={state} options={gridOptions}  onSearchComplete={searchCompleteHandler}/>)
}

DetailedCashReport.propTypes = {
    HotelID: PropTypes.any
}
export default DetailedCashReport
